import type { DocPageContent } from '../docs-content'

const page = {
  order: 12,
  slug: 'audio',
  title: 'Edición de audio',
  description:
    'Graba una voz en off, ajusta la ganancia y los fundidos del clip, el tono, el ecualizador por clip, el mezclador y los medidores, y la limpieza de silencios y muletillas.',
  category: 'Herramientas Creativas',
  related: ['properties', 'local-ai'],
  sections: [
    {
      title: 'Ajustar un clip',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Selecciona un clip y abre la pestaña **Audio** en Propiedades.',
        },
        {
          kind: 'table',
          headers: ['Control', 'Rango'],
          rows: [
            ['Ganancia', '-60 dB a +12 dB (−60 dB silencia el clip en la práctica)'],
            ['Entrada gradual / Salida gradual (Fade In / Fade Out)', '0 a 5 segundos cada uno'],
            ['Tono', 'Semitonos y centésimas, independiente de la velocidad'],
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'También puedes arrastrar la línea de volumen y los controladores de fundido directamente sobre el clip en la línea de tiempo, y la **Ganancia** se puede animar con fotogramas clave para subir o bajar a lo largo de un clip.',
        },
      ],
    },
    {
      title: 'Tono y timbre',
      blocks: [
        {
          kind: 'list',
          items: [
            'Los controles de **Tono** cambian el tono sin modificar la velocidad del clip. Para cambiar la velocidad, usa **Velocidad** en la pestaña Video.',
            'La sección **Ecualizador** ofrece a cada clip un editor de curva para dar forma al timbre.',
          ],
        },
      ],
    },
    {
      title: 'Mezclador y medidores',
      blocks: [
        {
          kind: 'list',
          items: [
            'Abre los **Medidores** de audio para observar los niveles estéreo y detectar recortes (clipping) mientras reproduces.',
            'Usa el **Mezclador** para equilibrar los niveles en todo el proyecto.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'El volumen del monitor en la vista previa afecta solo a la reproducción local y nunca cambia la mezcla exportada.',
        },
      ],
    },
    {
      title: 'Grabar una voz en off',
      blocks: [
        {
          kind: 'paragraph',
          text: 'El botón **Grabar voz en off** (micrófono) en la barra de herramientas de la línea de tiempo graba la narración en sincronía con tu proyecto. La captura se ejecuta **mientras se reproduce la línea de tiempo**, y la toma terminada queda en una nueva pista de audio anclada a la posición del cursor de reproducción donde comenzaste.',
        },
        {
          kind: 'steps',
          items: [
            'Mueve el cursor de reproducción hasta donde debe comenzar la voz en off.',
            'Haz clic en el botón de **micrófono**; la línea de tiempo comienza a reproducirse y empieza la grabación.',
            'Usa **Pausar grabación** para detener juntos el cursor de reproducción y el micrófono, **Reanudar grabación** para continuar, y **Detener y guardar** para finalizar y colocar el clip en una nueva pista de audio.',
            'Haz clic en **Cancelar grabación** (la ✕) para descartar la toma sin agregar nada.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'Abre **Ajustes del micrófono** (la ▾ junto al botón) para elegir un dispositivo de entrada y afinar la captura. El medidor de nivel ahí es en vivo, así que puedes revisar tu micrófono antes de grabar.',
        },
        {
          kind: 'table',
          headers: ['Ajuste', 'Qué hace'],
          rows: [
            ['Supresión de ruido', 'Reduce el ruido de fondo constante. Activado de forma predeterminada.'],
            ['Ganancia automática', 'Mantiene tu voz en un nivel constante. Activado de forma predeterminada.'],
            [
              'Silenciar línea de tiempo durante la grabación',
              'Silencia el monitor de la línea de tiempo para que el audio de los altavoces no se filtre en el micrófono. Activado de forma predeterminada.',
            ],
            [
              'Desfase de sincronización',
              'Adelanta o atrasa el clip grabado para compensar la latencia de entrada (hasta ±1000 ms).',
            ],
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Usa auriculares para que el audio de tu proyecto nunca sea captado por el micrófono; así podrás desactivar **Silenciar línea de tiempo durante la grabación** para escuchar la mezcla mientras narras.',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'La búsqueda (seeking) está deshabilitada durante la grabación, y presionar `Espacio` o llegar al final de la línea de tiempo finaliza la toma. Esto mantiene la grabación anclada al cursor de reproducción para que nunca pueda desincronizarse.',
        },
      ],
    },
    {
      title: 'Limpiar el diálogo',
      blocks: [
        {
          kind: 'list',
          items: [
            '**Eliminar silencios** encuentra rangos silenciosos y los corta; el ajuste **Silencio mínimo** controla cuánto debe durar un espacio, y el diálogo estima cuánto se eliminará.',
            '**Eliminar muletillas** detecta palabras como "eh" y "este" a partir de la transcripción y el audio, y las elimina.',
            'Ambas herramientas muestran los rangos que eliminarán para que puedas previsualizar antes de aplicar la edición.',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Una transcripción mejora la detección de muletillas, así que genera una primero para obtener los mejores resultados.',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
